import { createSelector } from '@reduxjs/toolkit'
import { getFlightItemsView } from 'checkout/selectors/view/flights'
import { selectTravellerCount } from './businessTravellerEmployeeSelectors'
import { FlightSearchBudgets, getFlightSearchBudgets } from 'lib/flights/flightUtils'
import { calculateFlightBudget, extractFlightRoutes } from 'businessTraveller/utils/calculateTripRequestBudget'

/**
 * Selector to get flight search budgets from Redux state.
 *
 * Note: This selector only reads existing budget data from the store.
 * The component should still use the hook to trigger fetching missing budgets.
 */
export const selectFlightSearchBudgets = createSelector(
  getFlightItemsView,
  (state: App.State) => state.businessTraveller.budgets,
  (state: App.State) => state.businessTraveller.flightSearchBudget,
  (flightItemsView, budgets, flightSearchBudgetCache): {
    flightSearchBudgets: Array<FlightSearchBudgets>
    perPersonTotalBudget: number
    isBudgetFetchPending: boolean
  } => {
    if (!flightItemsView.hasRequiredData) {
      return { flightSearchBudgets: [], perPersonTotalBudget: 0, isBudgetFetchPending: false }
    }

    const flights = extractFlightRoutes(flightItemsView.data)
    const { flightSearchBudgets, isBudgetFetchPending } = getFlightSearchBudgets(flights, flightSearchBudgetCache, budgets)

    const perPersonTotalBudget = flightSearchBudgets.reduce((acc, flight) => {
      return acc + (flight?.matchedBudget?.budget ?? 0)
    }, 0)

    return { flightSearchBudgets, perPersonTotalBudget, isBudgetFetchPending }
  },
)

/**
 * Selector to calculate flight budget info including isOutOfBudget.
 * Uses the shared calculateFlightBudget util.
 */
export const selectFlightBudgetInfo = createSelector(
  getFlightItemsView,
  selectFlightSearchBudgets,
  selectTravellerCount,
  (flightItemsView, flightSearchBudgetsResult, travellerCount) => {
    if (!flightItemsView.hasRequiredData) {
      return undefined
    }

    const flightBudget = flightSearchBudgetsResult.perPersonTotalBudget * travellerCount

    return calculateFlightBudget({
      flightViews: flightItemsView.data,
      flightSearchBudgets: flightSearchBudgetsResult.flightSearchBudgets,
      flightBudget,
    })
  },
)
